import { useEffect, useState } from "react";
import axios from "axios";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { CONTENT, CONTENT_FIELDS } from "../../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls =
  "mt-2 w-full border border-white/15 bg-[#050505] px-4 py-3 text-sm text-white outline-none transition-colors focus:border-[#0055FF]";

export default function ContentEditor({ token, onUnauthorized }) {
  const [values, setValues] = useState(() => ({ ...CONTENT }));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/content`)
      .then((r) => setValues((v) => ({ ...v, ...(r.data || {}) })))
      .catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/content`,
        { updates: values },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      Object.assign(CONTENT, values);
      toast.success("Content saved — live on the website now");
    } catch (e) {
      if (e.response?.status === 401) onUnauthorized?.();
      toast.error(e.response?.data?.detail || "Failed to save content");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="content-editor" className="mt-8">
      <p className="max-w-2xl text-sm leading-relaxed text-[#A3A3A3]">
        Edit the main text shown across the website — hero headline, about paragraphs, mission, vision, call-to-action,
        phone number and address. Changes go live as soon as you save.
      </p>
      <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
        {CONTENT_FIELDS.map((f) => (
          <div key={f.key} className={f.multiline ? "md:col-span-2" : ""}>
            <label className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/50">{f.label}</label>
            {f.multiline ? (
              <textarea
                data-testid={`content-input-${f.key}`}
                rows={3}
                value={values[f.key] ?? ""}
                onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                className={`${inputCls} resize-none`}
              />
            ) : (
              <input
                data-testid={`content-input-${f.key}`}
                value={values[f.key] ?? ""}
                onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                className={inputCls}
              />
            )}
          </div>
        ))}
      </div>
      <button
        data-testid="content-save-button"
        onClick={save}
        disabled={saving}
        className="mt-8 flex items-center gap-2 bg-[#0055FF] px-8 py-3.5 font-display text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#3377FF] disabled:opacity-60"
      >
        <Save size={15} /> {saving ? "Saving..." : "Save All Changes"}
      </button>
    </div>
  );
}
